import React, { useEffect, useState } from 'react';
import { Autocomplete } from '@mantine/core';
import { Species, useProductQuiz } from '../ProductQuizContext';
import { QuestionTitle } from './QuestionTitle';
import { QuestionNextButton } from './QuestionNextButton';
import { quizQuestionPage } from '../../../lib/analytics/events';
import { CAT_BREEDS, DOG_BREEDS } from '../Breeds';
import { useTranslation } from 'react-i18next';

const useBreeds = (species?: Species | null) => {
  const [breeds, setBreeds] = useState<string[]>([]);

  useEffect(() => {
    if (species === Species.Dog) {
      setBreeds(DOG_BREEDS);
    } else {
      setBreeds(CAT_BREEDS);
    }
  }, [species]);

  return breeds;
};

export const QuestionBreed: React.FC = () => {
  const { t } = useTranslation();
  useEffect(() => {
    quizQuestionPage.quizPetBreedPageEntered();
  }, []);

  const inputRef = React.useRef<HTMLInputElement>(null);
  const { quizAnswers, setQuizAnswers, nextQuestion } = useProductQuiz();
  const [autocompleteBreed, setAutocompleteBreed] = useState(quizAnswers.breed || '');

  const breeds = useBreeds(quizAnswers.species);
  const isExactMatch = breeds.some((breed) => breed.toLowerCase() === autocompleteBreed.toLowerCase());
  const suggestions = autocompleteBreed && autocompleteBreed.length >= 3 && !isExactMatch ? breeds : [];

  return (
    <>
      <QuestionTitle>{t('what_breed_is_name', { name: quizAnswers.petName })}</QuestionTitle>
      <Autocomplete
        autoFocus
        ref={inputRef}
        radius={'xl'}
        dropdownPosition="bottom"
        limit={10}
        styles={(theme) => ({
          input: { height: 56, fontSize: 16, paddingLeft: '2em', paddingRight: 10, enterkeyhint: 'next' },
          dropdown: {
            borderRadius: 24,
            fontSize: 16,
            padding: 0,
            display: suggestions.length === 0 ? 'none' : 'block',
            overflow: 'hidden',
          },
          itemsWrapper: {
            padding: 0,
            maxHeight: '25dvh',
            overflowY: 'auto',
            WebkitOverflowScrolling: 'touch',
            overscrollBehavior: 'contain',
          },
          item: {
            padding: '16px 20px',
            textAlign: 'center',
            borderBottom: `1px solid ${theme.colors['gray-400']}`,
            '&:last-of-type': {
              borderBottom: 'none',
            },
          },
        })}
        placeholder={t('search_for_a_breed')}
        data={suggestions}
        value={autocompleteBreed}
        onChange={(value) => {
          setAutocompleteBreed(value);
          setQuizAnswers({ ...quizAnswers, breed: value });
        }}
        onKeyDown={(event) => {
          if (event.key === 'Enter') {
            if (inputRef.current?.value) {
              quizQuestionPage.quizPetBreedNext(inputRef.current?.value ?? '');
              setQuizAnswers({ ...quizAnswers, breed: inputRef.current?.value });
              nextQuestion();
            }
          }
        }}
        transitionProps={
          quizAnswers.breed && quizAnswers.breed.length >= 2 ? { transition: 'fade', duration: 500 } : undefined
        }
      />
      <QuestionNextButton
        showOnDesktop={true}
        isDisabled={!quizAnswers.breed}
        event={() => quizQuestionPage.quizPetBreedNext(quizAnswers.breed ?? '')}
      />
    </>
  );
};
